// Admin → Installed Devices. How many pieces are on a customer's premises,
// and which ones.
//
// ⚠️ THIS IS NOT THE USER ACCESS REPORT AND MUST NOT BE CONFUSED WITH IT. That
// one reads the mapping tables and answers "what can this USER reach"; this one
// reads the premises. Location 18 has 37 pieces installed and the User Access
// sheet for the user who owns it shows 13 — he is allotted one of its three
// bedrooms. Both numbers are correct answers to different questions, and only
// one of them is an installation count. The reasoning, and the endpoint, are in
// core/admin/installedReport.
//
// ── Two scopes, one page ──────────────────────────────────────────────────
// A site is ONE request and lands instantly: that is the handover sheet, and
// the common case. "All locations" walks every location six at a time — around
// 117 requests — for the company-wide inventory, so it is chosen deliberately
// and shows progress while it runs. It is never the initial state.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Autocomplete, TextField,
  Alert, LinearProgress, Divider, Paper, Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApp } from '../../../core/state/AppContext';
import { getAllLocations } from '../../../core/api/api';
import { tokenFilter } from '../../../core/utils/search';
import ReportView, { ReportMeta } from '../../shared/ReportView';
import ReportActions from '../../shared/ReportActions';
import {
  loadSiteInstalled, loadEstateInstalled,
  siteInstalledReport, estateInstalledReport,
} from '../../../core/admin/installedReport';

// The "every location" choice, modelled as an option in the same picker rather
// than a separate switch: it is the same question at a different scope, and a
// mode toggle beside a picker is one control too many.
const ALL = { LocationID: '__all__', LocationName: 'All locations' };

const labelOf = (l) => (l === ALL || l.LocationID === ALL.LocationID
  ? 'All locations'
  : l.LocationWithName || l.LocationName || `Location ${l.LocationID}`);

const searchText = (l) => `${labelOf(l)} ${l.LocationID}`;

export default function InstalledDevicesScreen() {
  const { isSuperAdmin, companyName } = useApp();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [choice, setChoice] = useState(null);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  // Bumped on every read. A stale estate walk keeps firing progress callbacks
  // for a minute after the operator has picked something else — it has no way
  // to know — so both the progress line and the result are gated on it.
  const runId = useRef(0);
  // Read by collectEstateDevices between locations, so leaving the page does not
  // leave 117 requests running behind it.
  const stopped = useRef(false);
  useEffect(() => () => { stopped.current = true; }, []);

  useEffect(() => {
    getAllLocations()
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load the location list.'));
  }, []);

  const run = useCallback(async (pick) => {
    if (!pick) { setReport(null); setProgress(null); return; }
    runId.current += 1;
    const mine = runId.current;
    stopped.current = false;
    const mineStill = () => runId.current === mine && !stopped.current;

    setReport(null);
    setError('');
    setProgress({ label: 'Reading…' });

    try {
      const estate = pick.LocationID === ALL.LocationID;
      const devices = estate
        ? await loadEstateInstalled(
          (p) => { if (mineStill()) setProgress(p); },
          () => !mineStill(),
        )
        : await loadSiteInstalled(pick);

      if (!mineStill()) return;
      setReport(estate
        ? estateInstalledReport({ devices })
        : siteInstalledReport({ location: pick, devices }));
    } catch (e) {
      if (mineStill()) setError('That location could not be read.');
    } finally {
      if (runId.current === mine) setProgress(null);
    }
  }, []);

  if (!isSuperAdmin) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Super Admin only.</Alert></Box>;
  }

  // The letterhead is a session fact — core builds the model and knows nothing
  // about who is signed in.
  const sheet = report ? { ...report, brand: companyName || undefined } : null;
  const pct = progress && progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : null;

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>Installed Devices</Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{
        p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0,
      }}
      >
        <Autocomplete
          size="small"
          sx={{ width: 360 }}
          options={[ALL, ...locations]}
          value={choice}
          onChange={(_, v) => { setChoice(v); run(v); }}
          getOptionLabel={labelOf}
          isOptionEqualToValue={(o, v) => String(o.LocationID) === String(v.LocationID)}
          filterOptions={tokenFilter(searchText)}
          // ⚠️ NOT DECORATION — this supplies the option KEY. Location names
          // repeat ("Home" appears dozens of times), and MUI keys on the label
          // unless given something better, which drops and duplicates rows as
          // the list re-renders on each keystroke.
          renderOption={(props, option) => (
            <li {...props} key={String(option.LocationID)}>{labelOf(option)}</li>
          )}
          renderInput={(p) => (
            <TextField {...p} placeholder="Pick a location, or All locations…" />
          )}
        />
        {choice && !progress && (
          <Button size="small" onClick={() => run(choice)}>Re-read</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {sheet && <ReportActions report={sheet} onError={setError} />}
      </Box>

      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }}>{error}</Alert>}

      {progress && (
        <Box sx={{ px: 1, pb: 1, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5 }}>{progress.label || 'Reading…'}</Typography>
          <LinearProgress
            variant={pct == null ? 'indeterminate' : 'determinate'}
            value={pct == null ? undefined : pct}
          />
          <Typography color="text.secondary" sx={{ fontSize: 10.5, mt: 0.5 }}>
            One request per location, six at a time. Leaving this page stops it.
          </Typography>
        </Box>
      )}

      {!choice && !progress && (
        <Alert severity="info" sx={{ mx: 1 }}>
          Pick a location to count what is installed on the premises — every
          piece, whoever it is mapped to. This is not the same as User Access,
          which lists only what one user has been allotted.
        </Alert>
      )}

      {sheet && (
        <Paper
          variant="outlined"
          sx={{
            m: 1, mt: 0, p: 1, flex: 1, minHeight: 0, overflowY: 'auto',
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{sheet.title}</Typography>
          <ReportMeta report={sheet} />
          <Divider sx={{ my: 1 }} />
          <ReportView report={sheet} />
        </Paper>
      )}
    </Box>
  );
}
